const CARD_SELECTOR = '.feature-highlight-card';
const CARDS_LIST_SELECTOR = '.feature-highlight-cards';
const DESCRIPTION_SELECTOR = '[data-feature-highlight-card-description]';
const TOGGLE_SELECTOR = '.feature-highlight-card-toggle';
const SUMMARY_CLASS = 'feature-highlight-card-summary';
const DETAILS_CLASS = 'feature-highlight-card-details';
const ELLIPSIS_CLASS = 'feature-highlight-card-ellipsis';
const ANIMATING_CLASS = 'feature-highlight-card-description-animating';
const ANIMATION_FALLBACK_DELAY = 400;

// Width-bucketed char/word caps tuned at Bootstrap breakpoints (~3 visible lines per config).
// Split is fixed at init — no ResizeObserver — so aria-expanded and hidden state never drift.
const DEFAULT_MAX_WORDS = 20;
const DEFAULT_MAX_CHARS = 120;
const RESPONSIVE_SPLIT_CONFIGS = [
  { minDescriptionWidth: 340, maxWords: DEFAULT_MAX_WORDS, maxChars: DEFAULT_MAX_CHARS },
  { minDescriptionWidth: 300, maxWords: 17, maxChars: 100 },
  { minDescriptionWidth: 240, maxWords: 15, maxChars: 90 },
  { minDescriptionWidth: 210, maxWords: 12, maxChars: 70 },
  { minDescriptionWidth: 160, maxWords: 10, maxChars: 55 },
  { minDescriptionWidth: 115, maxWords: 6, maxChars: 36 },
  { minDescriptionWidth: 0, maxWords: 5, maxChars: 28 },
];

let nextDetailsId = 0;

function getDefaultSplitConfig(description) {
  const descriptionWidth = description?.getBoundingClientRect().width || 0;

  if (!descriptionWidth) {
    return {
      maxWords: DEFAULT_MAX_WORDS,
      maxChars: DEFAULT_MAX_CHARS,
    };
  }

  return RESPONSIVE_SPLIT_CONFIGS.find(({ minDescriptionWidth }) => descriptionWidth >= minDescriptionWidth);
}

function splitDescription(text, options) {
  const defaultConfig = (options && options.defaultConfig) || {
    maxWords: DEFAULT_MAX_WORDS,
    maxChars: DEFAULT_MAX_CHARS,
  };
  const maxWords = (options && options.maxWords) || defaultConfig.maxWords;
  const maxChars = (options && options.maxChars) || defaultConfig.maxChars;
  const words = text.trim().split(/\s+/).filter(Boolean);
  let charCount = 0;
  let splitIndex = words.length;

  for (let index = 0; index < words.length; index += 1) {
    const nextCharCount = charCount + (index > 0 ? 1 : 0) + words[index].length;

    if (index >= maxWords || nextCharCount > maxChars) {
      splitIndex = index;
      break;
    }

    charCount = nextCharCount;
  }

  return {
    summary: words.slice(0, splitIndex).join(' '),
    details: words.slice(splitIndex).join(' '),
  };
}

function setFeatureHighlightCardExpandedState(toggle, details, ellipsis, shouldExpand) {
  toggle.setAttribute('aria-expanded', String(shouldExpand));
  toggle.classList.toggle('collapsed', !shouldExpand);
  details.setAttribute('aria-hidden', String(!shouldExpand));
  details.hidden = !shouldExpand;
  ellipsis.hidden = shouldExpand;
}

function focusFeatureHighlightCardDetails(details) {
  details.focus({ preventScroll: true });
}

function prefersReducedMotion() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

function forceReflow(element) {
  return element.offsetHeight;
}

function animateDetailsToggle(toggle, description, details, ellipsis, shouldExpand) {
  if (prefersReducedMotion()) {
    setFeatureHighlightCardExpandedState(toggle, details, ellipsis, shouldExpand);

    if (shouldExpand) {
      focusFeatureHighlightCardDetails(details);
    }

    return;
  }

  const startHeight = description.getBoundingClientRect().height;

  description.style.height = `${startHeight}px`;
  description.classList.add(ANIMATING_CLASS);
  forceReflow(description);

  setFeatureHighlightCardExpandedState(toggle, details, ellipsis, shouldExpand);

  description.style.height = '';
  const targetHeight = description.getBoundingClientRect().height;

  description.style.height = `${startHeight}px`;
  forceReflow(description);
  description.style.height = `${targetHeight}px`;

  let fallbackTimeout;

  const finishAnimation = () => {
    description.removeEventListener('transitionend', handleTransitionEnd);
    window.clearTimeout(fallbackTimeout);
    description.classList.remove(ANIMATING_CLASS);
    description.style.height = '';

    if (shouldExpand) {
      focusFeatureHighlightCardDetails(details);
    }
  };

  const handleTransitionEnd = (event) => {
    if (event.target === description && event.propertyName === 'height') {
      finishAnimation();
    }
  };

  fallbackTimeout = window.setTimeout(finishAnimation, ANIMATION_FALLBACK_DELAY);
  description.addEventListener('transitionend', handleTransitionEnd);
}

function toggleFeatureHighlightCard(toggle, description, details, ellipsis) {
  const shouldExpand = toggle.getAttribute('aria-expanded') !== 'true';

  animateDetailsToggle(toggle, description, details, ellipsis, shouldExpand);
}

function initializeFeatureHighlightCardToggle(toggle, description, details, ellipsis) {
  toggle.addEventListener('click', () => toggleFeatureHighlightCard(toggle, description, details, ellipsis));
}

function initializeFeatureHighlightCard(card) {
  if (card.dataset.featureHighlightCardInitialized === 'true') {
    return;
  }

  card.dataset.featureHighlightCardInitialized = 'true';

  const description = card.querySelector(DESCRIPTION_SELECTOR);
  const toggle = card.querySelector(TOGGLE_SELECTOR);

  if (!description || !toggle) {
    return;
  }

  const maxWords = Number(card.dataset.featureHighlightCardMaxWords) || undefined;
  const maxChars = Number(card.dataset.featureHighlightCardMaxChars) || undefined;
  const defaultConfig = getDefaultSplitConfig(description);
  const { summary, details } = splitDescription(description.textContent, { defaultConfig, maxWords, maxChars });

  if (!details) {
    return;
  }

  description.textContent = '';

  const summaryElement = document.createElement('span');

  summaryElement.className = SUMMARY_CLASS;
  summaryElement.textContent = summary;

  description.append(summaryElement);

  // Real DOM span with aria-hidden — CSS ::after content can't be excluded from assistive tech.
  const ellipsisElement = document.createElement('span');

  ellipsisElement.className = ELLIPSIS_CLASS;
  ellipsisElement.setAttribute('aria-hidden', 'true');
  ellipsisElement.textContent = '…';

  description.append(ellipsisElement);

  nextDetailsId += 1;
  const detailsId = `feature-highlight-card-details-${nextDetailsId}`;

  const detailsElement = document.createElement('span');

  detailsElement.id = detailsId;
  detailsElement.className = DETAILS_CLASS;
  detailsElement.setAttribute('aria-hidden', 'true');
  detailsElement.setAttribute('tabindex', '-1');
  detailsElement.hidden = true;
  detailsElement.textContent = ` ${details}`;

  description.append(detailsElement);

  toggle.hidden = false;
  toggle.setAttribute('aria-controls', detailsId);
  initializeFeatureHighlightCardToggle(toggle, description, detailsElement, ellipsisElement);
}

export function initializeFeatureHighlightCards(root = document) {
  if (root instanceof Element && root.matches(CARD_SELECTOR)) {
    initializeFeatureHighlightCard(root);
  }

  root.querySelectorAll(CARD_SELECTOR).forEach((card) => {
    initializeFeatureHighlightCard(card);
  });
}

function scheduleFeatureHighlightCardsInitialization(root) {
  const initialize = () => initializeFeatureHighlightCards(root);

  if (typeof window.requestAnimationFrame !== 'function') {
    window.setTimeout(initialize, 0);
    return;
  }

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(initialize);
  });
}

function observeFeatureHighlightCardsList(list) {
  if (list.dataset.featureHighlightCardsObserved === 'true') {
    return;
  }

  list.dataset.featureHighlightCardsObserved = 'true';
  scheduleFeatureHighlightCardsInitialization(list);

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) {
          return;
        }

        scheduleFeatureHighlightCardsInitialization(node);
      });
    });
  });

  mutationObserver.observe(list, { childList: true, subtree: true });
}

function observeDynamicFeatureHighlightCardsLists() {
  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) {
          return;
        }

        if (node.matches(CARDS_LIST_SELECTOR)) {
          observeFeatureHighlightCardsList(node);
        }

        node.querySelectorAll(CARDS_LIST_SELECTOR).forEach((list) => {
          observeFeatureHighlightCardsList(list);
        });
      });
    });
  });

  mutationObserver.observe(document.body, { childList: true, subtree: true });
}

function installFeatureHighlightCardBehavior() {
  const setup = () => {
    document.querySelectorAll(CARDS_LIST_SELECTOR).forEach((list) => {
      observeFeatureHighlightCardsList(list);
    });

    observeDynamicFeatureHighlightCardsLists();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
}

installFeatureHighlightCardBehavior();
